import { createContext, useContext, useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { ExperimentProvider as CBHQExperimentProvider } from '@cbhq/experiments';

import { UserProfileContext } from '../../contexts/UserProfileContext';

import { useGetCiferExperiments } from './helpers/useGetCiferExperiments';

type ExperimentsProviderProps = {
  children: ReactNode;
};

export type WorkspaceExperimentAssignment = {
  name: string;
  group: 'control' | 'treatment';
};

type ExperimentsContextType = {
  isLoaded: boolean;
};

type TestExperimentsProviderProps = ExperimentsProviderProps & {
  isLoaded?: boolean;
  mockExperiments?: WorkspaceExperimentAssignment[];
};

const ExperimentsContext = createContext<ExperimentsContextType>({ isLoaded: false });
const EMPTY_EXPERIMENTS: WorkspaceExperimentAssignment[] = [];

// TODO Remove this once we have a real trackExposure function. No-op function to satisfy the trackExposure prop required by the CBHQExperimentProvider.
function noop() {}

export function ExperimentsProvider({ children }: ExperimentsProviderProps): ReactElement {
  const userContext = useContext(UserProfileContext);

  const subjectId = userContext?.userProfile?.id ?? '';
  const { loading, experiments } = useGetCiferExperiments(subjectId);

  const isLoaded = Boolean(subjectId) && !loading;
  const contextValue = useMemo<ExperimentsContextType>(() => ({ isLoaded }), [isLoaded]);

  return (
    <ExperimentsContext.Provider value={contextValue}>
      <CBHQExperimentProvider experiments={experiments} trackExposure={noop}>
        {children}
      </CBHQExperimentProvider>
    </ExperimentsContext.Provider>
  );
}

export function TestExperimentsProvider({
  children,
  isLoaded = true,
  mockExperiments = EMPTY_EXPERIMENTS,
}: TestExperimentsProviderProps): ReactElement {
  const contextValue = useMemo<ExperimentsContextType>(() => ({ isLoaded }), [isLoaded]);

  return (
    <ExperimentsContext.Provider value={contextValue}>
      <CBHQExperimentProvider experiments={mockExperiments} trackExposure={noop}>
        {children}
      </CBHQExperimentProvider>
    </ExperimentsContext.Provider>
  );
}

export function useExperiments(): ExperimentsContextType {
  return useContext(ExperimentsContext);
}
